import React, {
   forwardRef,
   ReactElement,
   ReactNode,
   Ref,
   useCallback,
   useMemo, useRef,
} from 'react';
import {
   FormApi,
   Form,
   Mutator,
   SubmissionErrors,
} from '../form';
import setFieldDataMutator from 'final-form-set-field-data';
import { AutoSaveHandler, runRequest } from './auto-save-handler/AutoSaveHandler';
import {
   clearSubmitErrors,
   setFormSubmitting,
   setInitialValue,
   setSaveErrors
} from './auto-save-handler/mutators';
import { Queue } from '../../../utils/Queue';

/**
 * {@docCategory AutoSaveForm}
 */
export interface AutoSaveFormProps<T = Record<string, any>> {

   /**
    * The underlying form name.  This is used to namespace form field ids for screen readers.
    */
   name: string;

   /**
    * Called when the form data needs to be saved.
    *
    * @param fieldName - The name of the field being saved
    * @param value - The value of the field being saved
    * @param values - The current form values
    * @param form - The form api
    * @returns For asynchronous submission, a promise that returns SubmissionErrors if any.  Otherwise,
    * for synchronous submission, the SubmissionErrors if any
    */
   onSave?: (fieldNames: Array<keyof T>, values: T, form: FormApi<T>) =>
      SubmissionErrors
      | Promise<SubmissionErrors | undefined>
      | undefined
      | void;

   /**
    * The initial values to display in the fields.
    */
   initialValues?: T;

   /**
    * Optional mutators
    */
   mutators?: Record<string, Mutator<T>>;

   /**
    * Determines if the underlying html form will fill it's parent containers height.
    *
    * @defaultvalue true
    */
   verticalFill?: boolean;

   /**
    * The children of the form
    */
   children?: ReactNode;
}

export const AutoSaveForm = forwardRef(function AutoSaveForm<T>(props: AutoSaveFormProps<T>,
                                                                ref: Ref<FormApi<T>>) {
   const propsOnSave = props.onSave;
   const queuedDataRef = useRef<Queue<keyof T>>(new Queue<keyof T>());
   const previouslySavedValuesRef = useRef<T | undefined>({} as T);

   const onSubmit = useCallback((values: T,
                     form: FormApi<T>) => {

      const fieldNames = form.getRegisteredFields() as Array<keyof T>;
      queuedDataRef.current.fromArray(fieldNames);
      return runRequest(propsOnSave)(form as FormApi<T,Partial<T>>, queuedDataRef, previouslySavedValuesRef)();
   },[propsOnSave]);

   const setFieldData = setFieldDataMutator as Mutator<T,Partial<T>>;
   const mutatorProps = props.mutators;
   const mutators = useMemo(() => ({
      setFieldData,
      setFormSubmitting,
      setInitialValue,
      clearSubmitErrors,
      setSaveErrors,
      ...mutatorProps
   }), [setFieldData, mutatorProps]);

   return (
      <Form<T>
         name={props.name}
         onSubmit={onSubmit}
         initialValues={props.initialValues}
         disableSaveMask={true}
         mutators={mutators}
         ref={ref}
      >
         <AutoSaveHandler<T> onSave={propsOnSave} initialValues={props.initialValues}>
            {props.children}
         </AutoSaveHandler>
      </Form>
   );
}
) as <T>(props: AutoSaveFormProps<T> & React.RefAttributes<FormApi<T>>,
          ref: Ref<FormApi<T>>) => ReactElement;

